import * as React from "react";

// MUI
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import AddIcon from "@mui/icons-material/Add";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import { grey } from "@mui/material/colors";

// Third Party
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useApolloClient } from "@apollo/client";
import { Link as RouterLink } from "react-router-dom";

// Internal
import Post from "./components/Post";
import FeedItemActions from "./components/FeedItemActions";
import PublicationCard from "../../components/PublicationCard";
import SharedCompose from "../../components/SharedCompose";
import SharedAppBar from "../../components/SharedAppBar";
import {
  GET_COMMUNITY_FEED,
  GET_USER_COLLECTIONS,
} from "../../data/graphql/queries";
import {
  CREATE_POST,
  REACT_POST,
  COMMENT_POST,
  FLAG_POST,
  REACT_PUBLICATION,
  COMMENT_PUBLICATION,
  CREATE_COLLECTION,
  ADD_ITEM_TO_COLLECTION,
} from "../../data/graphql/mutations";
import { GET_USER_PROFILE_BY_ID } from "../../data/graphql/queries";

import {
  PostType,
  PublicationType,
  ReactionTypeEnum,
  UserType,
  CollectionType,
} from "../../data/types";
import withRoot from "../../withRoot";

import en from "../../data/en.json";

// Number of feed items fetched per infinite-scroll page.
const PAGE_SIZE = 10;

// A single, server-ordered community feed entry. The backend `communityFeed`
// query returns a union of Post / Publication, discriminated by `__typename`.
type FeedItem =
  | { type: "post"; data: PostType }
  | { type: "publication"; data: PublicationType };

// Convert the raw union results into discriminated FeedItem entries.
function toFeedItems(items: any[]): FeedItem[] {
  return items.map((item) =>
    item.__typename === "Post"
      ? { type: "post", data: item as PostType }
      : { type: "publication", data: item as PublicationType }
  );
}

// Append `next` to `prev`, skipping any items already present. Posts and
// publications have independent id sequences, so the dedup key combines both.
function mergeFeed(prev: FeedItem[], next: FeedItem[]): FeedItem[] {
  const seen = new Set(prev.map((item) => `${item.type}-${item.data.id}`));
  return [
    ...prev,
    ...next.filter((item) => !seen.has(`${item.type}-${item.data.id}`)),
  ];
}

function Main() {
  const { user: auth0User } = useAuth0();
  const client = useApolloClient();
  const { data: UserProfileData } = useQuery(GET_USER_PROFILE_BY_ID, {
    variables: { user_auth0_id: auth0User?.sub },
  });

  const {
    loading: collectionsLoading,
    data: collectionsData,
    refetch: refetchCollections,
  } = useQuery(GET_USER_COLLECTIONS, {
    variables: {
      user_auth0_id: auth0User?.sub,
      include_private: true,
    },
  });

  const [createPost, { data: CreatePostData, error: CreatePostError }] =
    useMutation(CREATE_POST);

  const [reactPost, { data: ReactPostData, error: ReactPostError }] =
    useMutation(REACT_POST);

  const [commentPost, { data: CommentPostData, error: CommentPostError }] =
    useMutation(COMMENT_POST);

  const [flagPost, { error: FlagPostError, data: FlagPostData }] =
    useMutation(FLAG_POST);

  const [
    reactPublication,
    { data: ReactPublicationData, error: ReactPublicationError },
  ] = useMutation(REACT_PUBLICATION);

  const [
    commentPublication,
    { data: CommentPublicationData, error: CommentPublicationError },
  ] = useMutation(COMMENT_PUBLICATION);

  const [createCollection, { loading: createCollectionLoading }] =
    useMutation(CREATE_COLLECTION);

  const [addItemToCollection, { loading: addItemLoading }] = useMutation(
    ADD_ITEM_TO_COLLECTION
  );

  const [feed, setFeed] = React.useState<FeedItem[]>([]);
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const loadingRef = React.useRef(false);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const [user, setUser] = React.useState<UserType | null>(null);
  const [snackbarMessage, setSnackbarMessage] = React.useState<null | string>(
    null
  );

  // Collection-related state. A selected item can be either a Post or a
  // Publication; `type` carries the GraphQL item_type used by the collection
  // mutations.
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = React.useState<{
    type: "Post" | "Publication";
    data: PostType | PublicationType;
  } | null>(null);
  const [isCreatingCollection, setIsCreatingCollection] = React.useState(false);
  const [addingToCollectionId, setAddingToCollectionId] = React.useState<
    string | null
  >(null);
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [newCollectionTitle, setNewCollectionTitle] = React.useState("");
  const [newCollectionDescription, setNewCollectionDescription] =
    React.useState("");
  const [newCollectionPublic, setNewCollectionPublic] = React.useState(false);

  // Replace a post in the feed in place (after a reaction/comment mutation).
  const updatePostInFeed = (post: PostType) => {
    setFeed((prev) =>
      prev.map((item) =>
        item.type === "post" && item.data.id === post.id
          ? { type: "post", data: post }
          : item
      )
    );
  };

  // Replace a publication in the feed in place (after a reaction/comment mutation).
  const updatePublicationInFeed = (publication: PublicationType) => {
    setFeed((prev) =>
      prev.map((item) =>
        item.type === "publication" && item.data.id === publication.id
          ? { type: "publication", data: publication }
          : item
      )
    );
  };

  React.useEffect(() => {
    if (UserProfileData) {
      setUser(UserProfileData.userProfileById as UserType);
    }
  }, [UserProfileData]);

  React.useEffect(() => {
    if (CreatePostData) {
      const newPost: PostType = CreatePostData.createPost as PostType;
      setFeed((prev) => [{ type: "post", data: newPost }, ...prev]);
      setSnackbarMessage(en.community.postSuccess);
    }
  }, [CreatePostData]);

  // Initial load: fetch the first page of the unified community feed.
  React.useEffect(() => {
    let cancelled = false;
    setInitialLoading(true);

    client
      .query({
        query: GET_COMMUNITY_FEED,
        variables: { paging_offset: 0, paging_limit: PAGE_SIZE },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        if (cancelled) return;
        const items = toFeedItems((data?.communityFeed as any[]) || []);
        setFeed(items);
        setOffset(items.length);
        setHasMore(items.length >= PAGE_SIZE);
        setInitialLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setInitialLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client]);

  // Fetch the next page of the unified feed and append it. Guarded by a ref so
  // overlapping scroll events don't trigger duplicate fetches.
  const loadMore = React.useCallback(async () => {
    if (loadingRef.current) return;
    if (!hasMore) return;

    loadingRef.current = true;
    setLoadingMore(true);

    try {
      const { data } = await client.query({
        query: GET_COMMUNITY_FEED,
        variables: { paging_offset: offset, paging_limit: PAGE_SIZE },
        fetchPolicy: "network-only",
      });
      const items = toFeedItems((data?.communityFeed as any[]) || []);
      setFeed((prev) => mergeFeed(prev, items));
      setOffset((prev) => prev + items.length);
      if (items.length < PAGE_SIZE) setHasMore(false);
    } finally {
      loadingRef.current = false;
      setLoadingMore(false);
    }
  }, [client, hasMore, offset]);

  // Trigger loadMore when the sentinel near the bottom of the feed scrolls into
  // view.
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "400px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  React.useEffect(() => {
    if (ReactPostData) {
      let post: PostType = ReactPostData.reactPost as PostType;
      updatePostInFeed(post);
      setSnackbarMessage(en.community.reactPostSuccess);
    }
  }, [ReactPostData]);

  React.useEffect(() => {
    if (CommentPostData) {
      let post: PostType = CommentPostData.commentPost as PostType;
      updatePostInFeed(post);
      setSnackbarMessage(en.community.commentPostSuccess);
    }
  }, [CommentPostData]);

  React.useEffect(() => {
    if (ReactPublicationData) {
      let publication: PublicationType =
        ReactPublicationData.reactPublication as PublicationType;
      updatePublicationInFeed(publication);
      setSnackbarMessage(en.community.reactPublicationSuccess);
    }
  }, [ReactPublicationData]);

  React.useEffect(() => {
    if (CommentPublicationData) {
      let publication: PublicationType =
        CommentPublicationData.commentPublication as PublicationType;
      updatePublicationInFeed(publication);
      setSnackbarMessage(en.community.commentPublicationSuccess);
    }
  }, [CommentPublicationData]);

  React.useEffect(() => {
    if (FlagPostData) {
      let post: PostType = FlagPostData.flagPost as PostType;
      setFeed((prev) =>
        prev.filter(
          (item) => !(item.type === "post" && item.data.id === post.id)
        )
      );
      setSnackbarMessage(en.community.flagPostSuccess);
    }
  }, [FlagPostData]);

  React.useEffect(() => {
    if (CreatePostError) {
      setSnackbarMessage(en.community.postFailure);
    }
    if (ReactPostError) {
      setSnackbarMessage(en.community.reactPostFailure);
    }
    if (CommentPostError) {
      setSnackbarMessage(en.community.commentPostFailure);
    }
    if (FlagPostError) {
      setSnackbarMessage(en.community.flagPostFailure);
    }
    if (ReactPublicationError) {
      setSnackbarMessage(en.community.reactPublicationFailure);
    }
    if (CommentPublicationError) {
      setSnackbarMessage(en.community.commentPublicationFailure);
    }
  }, [
    CreatePostError,
    ReactPostError,
    CommentPostError,
    FlagPostError,
    ReactPublicationError,
    CommentPublicationError,
  ]);

  // Collection handlers. Items can be either Posts or Publications; the open
  // helper records which so the collection mutations send the right item_type.
  const openAddToCollection = (
    event: React.MouseEvent<HTMLElement>,
    type: "Post" | "Publication",
    data: PostType | PublicationType
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedItem({ type, data });
  };

  const handleAddToCollectionClick = (
    event: React.MouseEvent<HTMLElement>,
    post: PostType
  ) => openAddToCollection(event, "Post", post);

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleSelectCollection = async (collection: CollectionType) => {
    if (!selectedItem) {
      console.error("No item selected");
      return;
    }

    setAddingToCollectionId(collection.id);

    try {
      const result = await addItemToCollection({
        variables: {
          collection_uuid: collection.uuid,
          item_type: selectedItem.type,
          item_id: selectedItem.data.id,
        },
      });

      if (result.data?.addItemToCollection?.collectionItem) {
        console.log(
          `Successfully added item to collection "${collection.title}"`
        );
        await refetchCollections();
      } else if (result.data?.addItemToCollection?.errors?.length > 0) {
        console.error(
          "Error adding to collection:",
          result.data.addItemToCollection.errors
        );
      }
    } catch (error) {
      console.error("Error adding item to collection:", error);
    } finally {
      setAddingToCollectionId(null);
    }

    handleCloseMenu();
  };

  const handleCreateNewCollection = () => {
    if (!selectedItem) return;

    setNewCollectionTitle(
      `Collection - ${selectedItem.type} ${selectedItem.data.id}`
    );
    setNewCollectionDescription("");
    setNewCollectionPublic(false);
    setShowCreateModal(true);
    setAnchorEl(null);
  };

  const handleCreateCollection = async () => {
    if (!selectedItem || !newCollectionTitle.trim()) return;

    setIsCreatingCollection(true);

    try {
      const variables: any = {
        user_auth0_id: auth0User?.sub,
        title: newCollectionTitle.trim(),
        description: newCollectionDescription.trim() || null,
        public: newCollectionPublic,
      };

      if (selectedItem && selectedItem.data.id) {
        variables.item_type = selectedItem.type;
        variables.item_id = selectedItem.data.id;
      }

      const result = await createCollection({
        variables,
      });

      if (result.data?.createCollection) {
        console.log(
          "Collection created successfully:",
          result.data.createCollection
        );
        await refetchCollections();
        setShowCreateModal(false);
        setNewCollectionTitle("");
        setNewCollectionDescription("");
        setNewCollectionPublic(false);
        setSelectedItem(null);
      }
    } catch (error) {
      console.error("Error creating collection:", error);
    } finally {
      setIsCreatingCollection(false);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setNewCollectionTitle("");
    setNewCollectionDescription("");
    setNewCollectionPublic(false);
    setSelectedItem(null);
  };

  const handleCreatePost = (text: string, imageUrl: string | null) => {
    if (auth0User) {
      createPost({
        variables: {
          user_auth0_id: auth0User.sub,
          text: text,
          image_url: imageUrl,
        },
      });
    }
  };

  const handleReactPost = (postId: string) => {
    if (auth0User) {
      reactPost({
        variables: {
          user_auth0_id: auth0User.sub,
          reaction_type: ReactionTypeEnum.love,
          post_id: postId,
        },
      });
    }
  };

  const handleCommentPost = (postId: string, commentText: string) => {
    if (auth0User) {
      commentPost({
        variables: {
          user_auth0_id: auth0User.sub,
          post_id: postId,
          comment: commentText,
        },
      });
    }
  };

  const handleFlagPost = (postId: string) => {
    if (auth0User) {
      flagPost({
        variables: {
          user_auth0_id: auth0User.sub,
          post_id: postId,
        },
      });
    }
  };

  const handleReactPublication = (publicationId: string) => {
    if (auth0User) {
      reactPublication({
        variables: {
          user_auth0_id: auth0User.sub,
          reaction_type: ReactionTypeEnum.love,
          publication_id: publicationId,
        },
      });
    }
  };

  const handleCommentPublication = (
    publicationId: string,
    commentText: string
  ) => {
    if (auth0User) {
      commentPublication({
        variables: {
          user_auth0_id: auth0User.sub,
          publication_id: publicationId,
          comment: commentText,
        },
      });
    }
  };

  const userCollections: CollectionType[] =
    (collectionsData?.userCollections as CollectionType[]) || [];

  return (
    <React.Fragment>
      <Snackbar
        open={snackbarMessage !== null}
        autoHideDuration={3000}
        message={snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
      />
      <SharedAppBar returnUrl="/" />
      <Grid container justifyContent={"center"} direction="row" sx={{ mt: 5 }}>
        <Box
          alignItems="flex-start"
          sx={{
            borderLeft: 1,
            borderRight: 1,
            borderColor: grey[200],
            width: 750,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              padding: 2,
              paddingTop: 10,
              borderBottom: 1,
              borderColor: grey[200],
            }}
          >
            {en.common.home}
          </Typography>
          {auth0User && (
            <SharedCompose user={user} handleCreatePost={handleCreatePost} />
          )}

          {initialLoading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center", py: 6 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <React.Fragment>
              {feed.map((item: FeedItem) =>
                item.type === "post" ? (
                  <Post
                    key={`post-${item.data.id}`}
                    user={auth0User}
                    post={item.data}
                    handleReactPost={handleReactPost}
                    handleCommentPost={handleCommentPost}
                    handleFlagPost={handleFlagPost}
                    handleAddToCollectionClick={handleAddToCollectionClick}
                  />
                ) : (
                  <Box
                    key={`publication-${item.data.id}`}
                    sx={{
                      p: 2,
                      borderBottom: 1,
                      borderColor: grey[200],
                    }}
                  >
                    <RouterLink
                      to={`/publications/${item.data.uuid}`}
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <PublicationCard publication={item.data} />
                    </RouterLink>
                    <FeedItemActions
                      user={auth0User}
                      reactions={item.data.reactions}
                      comments={item.data.comments}
                      onReact={() => handleReactPublication(item.data.id)}
                      onComment={(commentText) =>
                        handleCommentPublication(item.data.id, commentText)
                      }
                      onAddToCollection={(e) =>
                        openAddToCollection(e, "Publication", item.data)
                      }
                    />
                  </Box>
                )
              )}

              {/* Sentinel observed to trigger loading the next page. */}
              <Box ref={sentinelRef} sx={{ height: 1 }} />

              {loadingMore && (
                <Box
                  sx={{ display: "flex", justifyContent: "center", py: 4 }}
                >
                  <CircularProgress size={28} />
                </Box>
              )}
            </React.Fragment>
          )}
        </Box>
      </Grid>

      {/* Collection Selection Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: 250,
          },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ px: 2, py: 1, fontWeight: "bold" }}
        >
          Select Collection
        </Typography>
        {userCollections.map((collection: CollectionType) => (
          <MenuItem
            key={collection.id}
            onClick={() => handleSelectCollection(collection)}
            disabled={addingToCollectionId === collection.id}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <ListItemText
              primary={collection.title}
              secondary={`${collection.itemsCount} items`}
            />
            {addingToCollectionId === collection.id && (
              <CircularProgress size={20} />
            )}
          </MenuItem>
        ))}
        <MenuItem
          onClick={handleCreateNewCollection}
          sx={{
            borderTop: `1px solid ${grey[200]}`,
          }}
        >
          <AddIcon sx={{ mr: 1 }} />
          <ListItemText primary="Create New Collection" />
        </MenuItem>
      </Menu>

      {/* Create Collection Modal */}
      <Modal
        open={showCreateModal}
        onClose={handleCloseCreateModal}
        aria-labelledby="create-collection-modal"
        aria-describedby="create-collection-form"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            border: "1px solid #ccc",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h6" component="h2">
              Create New Collection
            </Typography>
            <IconButton onClick={handleCloseCreateModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedItem && (
            <Box
              mb={3}
              p={2}
              sx={{ backgroundColor: grey[50], borderRadius: 1 }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {selectedItem.type === "Post"
                  ? "Adding post:"
                  : "Adding publication:"}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {selectedItem.type === "Post"
                  ? `${(selectedItem.data as PostType).text.substring(0, 50)}...`
                  : (selectedItem.data as PublicationType).title}
              </Typography>
            </Box>
          )}

          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            <TextField
              label="Collection Title"
              value={newCollectionTitle}
              onChange={(e) => setNewCollectionTitle(e.target.value)}
              fullWidth
              required
              variant="outlined"
              disabled={isCreatingCollection}
            />

            <TextField
              label="Description (Optional)"
              value={newCollectionDescription}
              onChange={(e) => setNewCollectionDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              disabled={isCreatingCollection}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={newCollectionPublic}
                  onChange={(e) => setNewCollectionPublic(e.target.checked)}
                  disabled={isCreatingCollection}
                />
              }
              label="Make this collection public"
            />

            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Button
                onClick={handleCloseCreateModal}
                disabled={isCreatingCollection}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateCollection}
                disabled={isCreatingCollection || !newCollectionTitle.trim()}
                variant="contained"
                startIcon={isCreatingCollection ? null : <AddIcon />}
              >
                {isCreatingCollection ? "Creating..." : "Create Collection"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </React.Fragment>
  );
}

export default withRoot(Main);
